/**
 * Forum Homepage - Store
 * 
 * Modern forum page with stats, sidebar, and category cards
 */

import React, { useEffect, useState } from 'react'
import { useStore } from '@/contexts/StoreContext'
import { useTheme } from '@/contexts/ThemeContext'
import forumStoreService, { type ForumCategory, type ForumStats as ForumStatsType } from '@/lib/services/forumStore.service'
import { Card, CardContent } from '@/components/ui/card'
import { MessageCircle } from 'lucide-react'
import PageHero from '@/components/common/PageHero'
import ForumStats from '@/components/forum/ForumStats'
import ForumSidebar from '@/components/forum/ForumSidebar'
import ForumCategoryCard from '@/components/forum/ForumCategoryCard'
import { useTranslation } from 'react-i18next';

import { Caixa } from '@/construtor/caixa';
export default function ForumPage() {
  const { t } = useTranslation();
  const { store } = useStore()
  const { config } = useTheme()
  const [categories, setCategories] = useState<ForumCategory[]>([])
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState<ForumStatsType>({
    usersOnline: 0,
    totalTopics: 0,
    totalPosts: 0,
    newestMember: null,
  })

  const [onlineUsers, setOnlineUsers] = useState<Array<{ 
    id: string; 
    name: string; 
    nick?: string;
    badge: string;
    badges?: Array<{
      id: string;
      name: string;
      icon: string;
      color: string;
      style?: string;
      animation?: string;
    }>;
  }>>([])

  const [recentActivity, setRecentActivity] = useState<Array<{
    id: string
    type: 'new_topic' | 'new_post' | 'reaction'
    user: string
    action: string
    time: string
  }>>([])

  const [topContributors, setTopContributors] = useState<Array<{ 
    name: string; 
    posts: number;
    badges?: Array<{
      id: string;
      name: string;
      icon: string;
      color: string;
      style?: string;
      animation?: string;
    }>;
  }>>([])

  const [badges, setBadges] = useState<Array<{ id: string; name: string; description: string; icon: string; color: string; style?: string; animation?: string; type: string }>>([])
  const [badgeEarners, setBadgeEarners] = useState<Array<{ id: string; playerName: string; badgeName: string; badgeIcon: string; badgeColor: string; badgeStyle?: string; badgeAnimation?: string; timeAgo: string }>>([])

  useEffect(() => {
    if (store?.id) {
      loadCategories()
      loadStats()
      loadOnlineUsers()
      loadRecentActivity()
      loadTopContributors()
      loadBadges()
      loadBadgeEarners()
    }
  }, [store])

  async function loadCategories() {
    if (!store?.id) return
    
    try {
      setLoading(true)
      const data = await forumStoreService.listCategories(store.id)
      setCategories(data)
    } catch (error) {
      console.error('Erro ao carregar categorias:', error)
    } finally {
      setLoading(false)
    }
  }

  async function loadStats() {
    if (!store?.id) return
    
    try {
      const data = await forumStoreService.getForumStats(store.id)
      setStats(data)
    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error)
    }
  }

  async function loadOnlineUsers() {
    if (!store?.id) return
    
    try {
      const users = await forumStoreService.getOnlineUsers(store.id)
      setOnlineUsers(
        users
          .filter(u => u.name) // Filter out users without name
          .map(u => ({ 
            id: u.id, 
            name: u.name,
            nick: u.nick,
            badge: u.roleName,
            badges: u.badges
          }))
      )
    } catch (error) {
      console.error('Erro ao carregar usuários online:', error)
    }
  }

  async function loadRecentActivity() {
    if (!store?.id) return
    
    try {
      const activities = await forumStoreService.getRecentActivity(store.id)
      setRecentActivity(activities)
    } catch (error) {
      console.error('Erro ao carregar atividade recente:', error)
    }
  }

  async function loadTopContributors() {
    if (!store?.id) return
    
    try {
      const contributors = await forumStoreService.getTopContributors(store.id)
      setTopContributors(contributors)
    } catch (error) {
      console.error('Erro ao carregar top colaboradores:', error)
    }
  }

  async function loadBadges() {
    if (!store?.id) return
    
    try {
      const badgesData = await forumStoreService.getForumBadges(store.id)
      setBadges(badgesData)
    } catch (error) {
      console.error('Erro ao carregar badges:', error)
    }
  }

  async function loadBadgeEarners() {
    if (!store?.id) return
    
    try {
      const earners = await forumStoreService.getRecentBadgeEarners(store.id)
      setBadgeEarners(earners)
    } catch (error) {
      console.error('Erro ao carregar earners:', error)
    }
  }

  if (loading) {
    return (
      <div>
        <PageHero
          title="Fórum"
          breadcrumbs={[{ label: 'Fórum' }]}

        />
        <div className="container mx-auto px-4 md:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-center py-12">
            <div className="relative">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
              <div className="absolute inset-0 rounded-full bg-primary/20 blur-xl animate-pulse"></div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div>
      {/* Hero */}
      <PageHero
        title="Fórum da Comunidade"
        breadcrumbs={[{ label: 'Fórum' }]}

      />

      <div className="container mx-auto px-4 md:px-6 lg:px-8 py-8 -mt-24 pt-24 relative z-10">

        {/* Stats */}
        <Caixa id="forum.estatisticas" envolver>
        <ForumStats
          usersOnline={stats.usersOnline}
          totalTopics={stats.totalTopics}
          totalPosts={stats.totalPosts}
          newestMember={stats.newestMember ?? undefined}
        />
        </Caixa>

        {/* Main Content with Sidebar */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Categories */}
          <Caixa id="forum.categorias">
          <div className="lg:col-span-2 space-y-6">
            {categories.length === 0 ? (
              <Caixa id="forum.vazio" miolo>
              <Card className="border-card-border">
                <CardContent className="p-12">
                  <div className="flex flex-col items-center gap-4 text-center">
                    <div className="w-20 h-20 rounded-2xl bg-primary/10 flex items-center justify-center">
                      <MessageCircle className="w-10 h-10 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-foreground mb-2">
                        {t('store.forum.emptyCategoriesTitle')}
                      </h3>
                      <p className="text-muted-foreground text-lg">
                        {t('store.forum.emptyCategoriesBody')}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              </Caixa>
            ) : (
              categories.map((category) => (
                <Caixa id="forum.categoria" repetida envolver key={category.id}>
                <ForumCategoryCard key={category.id} category={category} />
                </Caixa>
              ))
            )}
          </div>
          </Caixa>

          {/* Sidebar */}
          <Caixa id="forum.lateral">
          <div className="lg:col-span-1">
            <ForumSidebar
              badges={badges}
              badgeEarners={badgeEarners}
              onlineUsers={onlineUsers}
              recentActivity={recentActivity}
              topContributors={topContributors}
            />
          </div>
          </Caixa>
        </div>
      </div>
    </div>
  )
}
